import { useCallback } from "react";
import type { Space } from "@al-life/types";
import { api } from "@/lib/api";
import { useCachedResource } from "@/lib/apiCache";

type Status = "loading" | "success" | "notfound" | "error";

/**
 * Load a space by slug (`GET /spaces/{slug}`), cached stale-while-revalidate:
 * repeat visits paint instantly from the last-known space doc while a silent
 * refetch confirms it. `status` keeps its original contract — "success" now
 * means "we have a space to show" (cached or fresh).
 */
export function useSpace(slug: string) {
  const fetcher = useCallback(() => api.spaces.get(slug), [slug]);
  const { data, phase, httpStatus, refresh } = useCachedResource<Space>(`${slug}:space`, fetcher);

  const status: Status = data
    ? "success"
    : phase === "error"
      ? httpStatus === 404
        ? "notfound"
        : "error"
      : "loading";

  return { space: data, status, reload: refresh };
}
